import { Activity, useState, type CSSProperties, type ReactNode } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import cometChatLogo from '../../assets/cometchat_logo_dark.svg';
import { EXPANDED_QUERY, useMediaQuery } from '../../hooks/useMediaQuery';
import { listRouteOf, type ListRoute } from '../../routes';
import { useCalls } from '../../calls/calls-context';
import { AccountMenu } from '../AccountMenu/AccountMenu';
import { MdIcon, type MdIconName } from '../Icon/MdIcon';
import './AppShell.css';
import './LobbyMaterial.css';

/**
 * The signed-in shell: Material 3's list-detail canonical layout.
 *
 *   >= 840px  History / Users / Groups as primary tabs in a list pane on the
 *             left, and the meeting lobby always on the right. History opens
 *             by default. Call details open in a dialog, not a third pane.
 *   <  840px  one pane at a time, switched by a navigation bar: Meeting,
 *             History, Users, Groups (plan F1.4).
 *
 * The meeting must keep its place in the tree. Home owns the live call
 * container, so if a resize moved it, React would remount it and drop the
 * call. It always renders as the only child of <main>, and the layout around
 * it only changes CSS. During a call the lists and the bar go away and the call
 * takes the viewport, as before.
 *
 * Below 840px the meeting unmounts while a list tab is showing, which is what
 * the old routes did. Otherwise a hidden lobby could keep the camera on. The
 * lists are the other way round: they stay mounted once visited (ListPanes).
 */

const LIST_TABS: { to: string; label: string }[] = [
  { to: '/history', label: 'History' },
  { to: '/users', label: 'Users' },
  { to: '/groups', label: 'Groups' },
];

const NAV_DESTINATIONS: { to: string; label: string; icon: MdIconName }[] = [
  { to: '/meeting', label: 'Meeting', icon: 'videocam' },
  { to: '/history', label: 'History', icon: 'history' },
  { to: '/users', label: 'Users', icon: 'person' },
  { to: '/groups', label: 'Groups', icon: 'group' },
];

export function AppShell({
  lists,
  meeting,
  inCall,
  onLogout,
}: {
  /** The list tabs' screens. Any non-list path shows History in the pane. */
  lists: Record<ListRoute, ReactNode>;
  meeting: ReactNode;
  inCall: boolean;
  onLogout: () => void;
}) {
  const expanded = useMediaQuery(EXPANDED_QUERY);
  const { pathname } = useLocation();
  const listRoute = listRouteOf(pathname);
  const calls = useCalls();
  // A ringing 1:1 call needs Home mounted, because Home joins it once it is
  // answered. Wide screens always have Home; on narrow ones it takes the
  // screen while a call rings (the ringing card covers it anyway).
  const ringing = calls.phase.kind !== 'idle' || calls.joinRequest !== null;

  const showLists =
    !inCall && (expanded || (listRoute !== null && !ringing));
  const showMeeting = inCall || expanded || listRoute === null || ringing;
  // On wide screens every non-list path (/, /meeting, an invite link) shows
  // History, so its tab is the active one there.
  const activeList = listRoute ?? 'history';

  return (
    <div
      className={[
        'app-shell',
        expanded ? 'app-shell--expanded' : 'app-shell--compact',
        inCall ? 'app-shell--in-call' : '',
      ].join(' ')}>
      <aside className="app-shell__lists" hidden={!showLists}>
        {expanded && (
          <>
            <header className="app-shell__pane-header">
              <span
                className="app-shell__logo"
                role="img"
                aria-label="CometChat"
                style={{ '--app-logo': `url(${cometChatLogo})` } as CSSProperties}
              />
              <AccountMenu onLogout={onLogout} />
            </header>
            <nav className="md-tabs" aria-label="Lists">
              {LIST_TABS.map(tab => {
                const active = `/${activeList}` === tab.to;
                return (
                  <NavLink
                    key={tab.to}
                    to={tab.to}
                    aria-current={active ? 'page' : undefined}
                    className={`md-tabs__tab${active ? ' is-active' : ''}`}>
                    <span className="md-tabs__label">{tab.label}</span>
                  </NavLink>
                );
              })}
            </nav>
          </>
        )}
        <div className="app-shell__list-body">
          <ListPanes lists={lists} active={activeList} shown={showLists} />
        </div>
      </aside>

      <main className="app-shell__meeting" hidden={!showMeeting}>
        {showMeeting && meeting}
      </main>

      {!expanded && !inCall && !ringing && (
        <nav className="md-navbar" aria-label="Main">
          {NAV_DESTINATIONS.map(d => (
            <NavLink
              key={d.to}
              to={d.to}
              className={({ isActive }) =>
                `md-navbar__item${isActive || (d.to === '/meeting' && listRoute === null) ? ' is-active' : ''}`
              }>
              <span className="md-navbar__indicator">
                <MdIcon name={d.icon} />
              </span>
              <span className="md-navbar__label">{d.label}</span>
            </NavLink>
          ))}
        </nav>
      )}
    </div>
  );
}

/**
 * The list tabs, each mounted on its first visit and then kept, so going back
 * to one shows its rows at once instead of an empty list. That holds across
 * the Meeting tab on narrow screens and across a call too.
 *
 * History and Users sit in an <Activity>. Hidden, they keep their state but
 * their effects are disconnected; shown again, the effects re-run, and that
 * re-run is what refetches their rows in the background (see History.tsx, and
 * `disableLoadingState` in Users.tsx).
 *
 * Groups is only hidden with CSS. The UIKit groups list empties itself before
 * every fetch, so the re-run on show would blank it. Kept mounted, its group
 * listeners keep it current instead.
 */
function ListPanes({
  lists,
  active,
  shown,
}: {
  lists: Record<ListRoute, ReactNode>;
  active: ListRoute;
  shown: boolean;
}) {
  const [visited, setVisited] = useState<readonly ListRoute[]>([]);
  if (shown && !visited.includes(active)) {
    // Set while rendering (React's "adjusting state on a prop change"), so the
    // tab is there in this same commit.
    setVisited([...visited, active]);
  }
  return visited.map(route => {
    const visible = shown && route === active;
    return route === 'groups' ? (
      <div key={route} style={{ display: visible ? 'contents' : 'none' }}>
        {lists[route]}
      </div>
    ) : (
      <Activity key={route} mode={visible ? 'visible' : 'hidden'}>
        {lists[route]}
      </Activity>
    );
  });
}
